import React, { ReactNode, useContext, useEffect, useState } from 'react';

import ContractedServicesContext from '@totem/components/userContext/contractedServicesContext/contractedServicesContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ContractedServices } from '@totem/types/contractedServices';
import { isNotNull } from '@totem/utilities/common';
import { USER_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children?: ReactNode;
};

// Holds data only — there is no UI for contracted services yet.
const ContractedServicesContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [data, setData] = useState<ContractedServices>(null);

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  const handleRefresh = () => {
    setRefreshData(true);
  };

  const organizationHasService = (service: string): boolean => {
    if (isNotNull(data)) {
      return data.services.organization.services.includes(service);
    }
    return false;
  };

  const organizationHasApiSiteType = (apiSiteType: string): boolean => {
    if (isNotNull(data)) {
      return data.apiSites.organization.apiTypes.includes(apiSiteType);
    }
    return false;
  };

  const buildingHasService = (buildingId: string, service: string): boolean => {
    if (isNotNull(data)) {
      const foundBuilding = data.services.buildings.find(
        (chk) => chk.id === buildingId,
      );
      if (isNotNull(foundBuilding)) {
        return foundBuilding.services.includes(service);
      }
    }
    return false;
  };

  const buildingHasApiSiteType = (
    buildingId: string,
    apiSiteType: string,
  ): boolean => {
    if (isNotNull(data)) {
      const foundBuilding = data.apiSites.buildings.find(
        (chk) => chk.id === buildingId,
      );
      if (isNotNull(foundBuilding)) {
        return foundBuilding.apiTypes.includes(apiSiteType);
      }
    }
    return false;
  };

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      fetch(`${USER_ENDPOINT}/contractedServices`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: ContractedServices) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  return (
    <ContractedServicesContext.Provider
      value={{
        loading: isLoading,
        data,
        doRefresh: handleRefresh,
        organizationHasService,
        organizationHasApiSiteType,
        buildingHasService,
        buildingHasApiSiteType,
      }}
    >
      <div>{children}</div>
    </ContractedServicesContext.Provider>
  );
};

export default ContractedServicesContainer;
